import { createFileRoute } from '@tanstack/react-router';
import { DocTable, RolePill, InfoCallout } from '../../components/documentation';
import styles from '../../components/documentation/DocContent.module.css';

export const Route = createFileRoute('/documentation/tolerances')({
  component: TolerancesPage,
});

function TolerancesPage() {
  return (
    <>
      <div className={styles.breadcrumb}>Building with Interlucent</div>
      <h1 className={styles.pageTitle}>Timing &amp; Resilience</h1>
      <p className={styles.pageSubtitle}>
        Control how sessions behave when things take time or go wrong.
      </p>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="overview">
          Overview
        </h2>
        <p className={styles.prose}>
          Tolerances control how long an agent waits at each phase of the
          connection lifecycle. All values are in seconds. All are optional — the
          platform applies defaults when absent.
        </p>
        <p className={styles.prose}>
          Tolerances are declared in the admission token's{' '}
          <code className={styles.mono}>tol</code> claim and passed through to
          the access token without modification. The platform reads them
          when the agent joins and starts the corresponding timers.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="fields-reference">
          Fields Reference
        </h2>
        <DocTable
          columns={[
            { header: 'Field', accessor: 'field', mono: true, nowrap: true },
            { header: 'Default', accessor: 'default' },
            { header: 'Applies to', accessor: 'applies' },
            { header: 'Description', accessor: 'description' },
          ]}
          data={[
            {
              field: 'rendezvous_tolerance_seconds',
              default: '30',
              applies: 'BA, WA',
              description:
                'How long this agent waits for the GPU worker to connect. If the timer fires before the worker arrives, the session ends for this participant.',
            },
            {
              field: 'linger_tolerance_seconds',
              default: '0',
              applies: 'BA, WA',
              description:
                'How long this agent stays alone after the other participant leaves normally. 0 = leave immediately. Use this to keep a worker alive between sessions.',
            },
            {
              field: 'flexible_presence_allowance_seconds',
              default: '0',
              applies: 'BA, WA',
              description:
                "Reconnection grace period. If this agent disconnects unexpectedly (e.g. a network drop), their seat is held for this many seconds. Unlike linger, this covers unexpected disconnections rather than normal departures.",
            },
            {
              field: 'queue_wait_tolerance_seconds',
              default: '0',
              applies: <RolePill role="BA" />,
              description:
                'How long the browser waits for GPU availability after requesting a session.',
            },
            {
              field: 'webrtc_negotiation_tolerance_seconds',
              default: '10',
              applies: <RolePill role="BA" />,
              description:
                'How long the browser waits for WebRTC to connect after rendezvous is satisfied.',
            },
          ]}
        />

        <InfoCallout variant="info">
          <strong>Linger vs. flexible presence:</strong>{' '}
          <code className={styles.mono}>linger_tolerance_seconds</code> applies
          when the other participant leaves normally.{' '}
          <code className={styles.mono}>flexible_presence_allowance_seconds</code>{' '}
          (your reconnection grace period) applies when <em>this</em> agent
          disconnects unexpectedly.
        </InfoCallout>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="phase-timeline">
          Browser Agent Phase Timeline
        </h2>
        <p className={styles.prose}>
          Each phase's timer starts when the previous phase completes. The
          following diagram shows the progression for a browser agent.
        </p>
        <div className={styles.timeline}>
          <pre className={styles.timelinePre}>{`JOIN ─── [Queue Wait] ─── [Rendezvous] ─── [WebRTC Negotiation] ─── CONNECTED
                                                                        ├── disconnect ─── [Flexible Presence]
                                                                        └── alone ──────── [Linger]`}</pre>
        </div>
        <p className={styles.prose}>
          If a timer fires before its condition is met, the agent is removed from
          the session with a descriptive reason (e.g.{' '}
          <code className={styles.mono}>queue_wait_timeout</code>,{' '}
          <code className={styles.mono}>rendezvous_timeout</code>,{' '}
          <code className={styles.mono}>
            flexible_presence_allowance_exceeded
          </code>
          ).
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="common-patterns">
          Common Patterns
        </h2>

        <h3 className={styles.subsectionTitle} id="fail-fast">
          Fail fast
        </h3>
        <p className={styles.prose}>
          For latency-sensitive applications, keep all tolerances low. A short{' '}
          <code className={styles.mono}>rendezvous_tolerance_seconds</code>{' '}
          (e.g. 10) combined with{' '}
          <code className={styles.mono}>
            webrtc_negotiation_tolerance_seconds: 5
          </code>{' '}
          ensures the user gets an error quickly rather than waiting in an
          uncertain state.
        </p>

        <h3 className={styles.subsectionTitle} id="survive-disruptions">
          Survive disruptions
        </h3>
        <p className={styles.prose}>
          For resilient sessions, increase{' '}
          <code className={styles.mono}>
            flexible_presence_allowance_seconds
          </code>{' '}
          (your reconnection grace period) to survive network hiccups (e.g. 300
          seconds = 5 minutes). Pair with{' '}
          <code className={styles.mono}>linger_tolerance_seconds: 30</code> so
          the worker doesn't immediately exit if the browser briefly drops.
        </p>

        <h3 className={styles.subsectionTitle} id="gpu-wait">
          Wait for GPU availability
        </h3>
        <p className={styles.prose}>
          When using on-demand GPU workers (not pre-provisioned pools), set{' '}
          <code className={styles.mono}>queue_wait_tolerance_seconds</code> to
          match your expected provisioning latency. A cold-start worker may take
          60–120 seconds to become available.
        </p>

        <InfoCallout variant="tip">
          Combine <code className={styles.mono}>queue_wait_tolerance_seconds</code>{' '}
          with the <code className={styles.mono}>sjr</code> (Swift Job Request)
          flag to submit the GPU availability request during token exchange,
          reducing total wait time by one round-trip.
        </InfoCallout>
      </section>
    </>
  );
}
